import React, { useState } from "react";
import { api, imgUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { toast } from "sonner";
import { Upload, X, Check } from "lucide-react";

const initialForm = {
  name: "",
  email: "",
  size: "",
  other_size_note: "",
  location: "",
  needed_by: "",
  description: "",
  reference_image_urls: [],
};

const Commissions = () => {
  const { settings } = useSettings();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const sizes = (settings.commission_prices || []).filter((p) => p.enabled);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/commission-attachment", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.url);
      }
      setForm((f) => ({
        ...f,
        reference_image_urls: [...f.reference_image_urls, ...uploaded],
      }));
      toast.success(
        uploaded.length === 1 ? "Reference photo added" : `${uploaded.length} photos added`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeRef = (url) =>
    setForm((f) => ({
      ...f,
      reference_image_urls: f.reference_image_urls.filter((u) => u !== url),
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.size || !form.description) {
      toast.error("Please fill in name, email, size, and your vision.");
      return;
    }
    setSending(true);
    try {
      await api.post("/commissions", form);
      setSubmitted(true);
      setForm(initialForm);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 lg:px-20 py-32 max-w-2xl mx-auto text-center" data-testid="commission-thank-you">
        <Check size={40} strokeWidth={1.4} className="mx-auto text-[#3B4A3F]" />
        <div className="label-eyebrow mt-6">Request received</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A1F1B] mt-3 leading-[1]">
          Thank you.
        </h1>
        <p className="mt-6 text-[#4E5C50] leading-relaxed">
          Your commission request has reached the studio. I&apos;ll be in touch
          via email within 24 hours to talk through the details.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="btn-secondary mt-10"
          data-testid="commission-another"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <div data-testid="page-commissions">
      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-12">
        <div className="max-w-3xl">
          <div className="label-eyebrow mb-4">Commissions</div>
          <h1 className="font-serif-display text-5xl md:text-7xl text-[#1A1F1B] leading-[1]">
            A painting,
            <br />
            <em className="italic text-[#3B4A3F]">made for you.</em>
          </h1>
          {settings.commission_intro && (
            <p className="mt-8 text-base md:text-lg text-[#4E5C50] leading-relaxed max-w-2xl" data-testid="commission-intro">
              {settings.commission_intro}
            </p>
          )}
        </div>
      </section>

      {/* PROCESS */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-[#EFECE5] grain border-y border-[#D8D3CC]">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="label-eyebrow mb-3">The Process</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1F1B] leading-tight">
              How a commission unfolds.
            </h2>
            <p className="mt-6 text-sm text-[#4E5C50] leading-relaxed">
              From your first note to your finished painting — here&apos;s
              what to expect.
            </p>
          </div>
          <ol className="md:col-span-8 space-y-6" data-testid="commission-process">
            {(settings.commission_process_steps || []).map((step, i) => (
              <li
                key={i}
                className="flex gap-5 pb-6 border-b border-[#D8D3CC] last:border-b-0"
                data-testid={`commission-step-${i}`}
              >
                <div className="font-serif-display text-3xl text-[#3B4A3F] leading-none w-12 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-[#4E5C50] leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PRICING */}
      {sizes.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24" data-testid="commission-pricing">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="label-eyebrow mb-3">Sizes & Pricing</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1F1B]">
                Choose your canvas.
              </h2>
            </div>
            {settings.commission_price_note && (
              <p className="text-sm text-[#4E5C50] max-w-md leading-relaxed">
                {settings.commission_price_note}
              </p>
            )}
          </div>

          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-l border-t border-[#D8D3CC]"
            data-testid="commission-price-grid"
          >
            {sizes.map((p) => (
              <div
                key={p.size}
                className="border-r border-b border-[#D8D3CC] p-6 bg-[#F7F6F2] hover:bg-[#EFECE5] transition-colors"
                data-testid={`commission-price-${p.size}`}
              >
                <div className="font-serif-display text-3xl text-[#1A1F1B]">
                  {p.size}
                </div>
                <div className="label-eyebrow mt-3 text-[10px]">Inches</div>
                <div className="font-serif-display text-2xl text-[#3B4A3F] mt-4">
                  {p.price_text || "Inquire"}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FORM */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-[#EFECE5] border-t border-[#D8D3CC]">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="label-eyebrow mb-3">Request Form</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1F1B] leading-tight">
              Tell me what you&apos;re imagining.
            </h2>
            <p className="mt-6 text-sm text-[#4E5C50] leading-relaxed">
              The more detail the better — but don&apos;t worry about getting
              everything right. We&apos;ll talk it through together over email.
            </p>
          </div>
          <form onSubmit={submit} className="md:col-span-8 space-y-8 bg-[#F7F6F2] p-8 md:p-10 border border-[#D8D3CC]" data-testid="commission-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="label-eyebrow">Your Name</label>
                <input
                  className="earth"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  data-testid="commission-input-name"
                />
              </div>
              <div>
                <label className="label-eyebrow">Email</label>
                <input
                  className="earth"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  data-testid="commission-input-email"
                />
              </div>
            </div>

            <div>
              <label className="label-eyebrow">Size</label>
              <select
                className="earth"
                name="size"
                value={form.size}
                onChange={onChange}
                required
                data-testid="commission-input-size"
              >
                <option value="">Choose a size…</option>
                {sizes.map((p) => (
                  <option key={p.size} value={p.size}>
                    {p.size} {p.price_text ? `— ${p.price_text}` : ""}
                  </option>
                ))}
                <option value="Other">Other (I&apos;ll send a quote)</option>
              </select>
              {form.size === "Other" && (
                <div className="mt-4">
                  <label className="label-eyebrow">Tell me what size you have in mind</label>
                  <input
                    className="earth"
                    name="other_size_note"
                    value={form.other_size_note}
                    onChange={onChange}
                    placeholder="e.g., 40x60, triptych, mural-scale…"
                    data-testid="commission-input-other-size"
                  />
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className="label-eyebrow">Outside the US? Location</label>
                <input
                  className="earth"
                  name="location"
                  value={form.location}
                  onChange={onChange}
                  placeholder="For shipping calculation"
                  data-testid="commission-input-location"
                />
              </div>
              <div>
                <label className="label-eyebrow">When do you need this by?</label>
                <input
                  className="earth"
                  type="date"
                  name="needed_by"
                  value={form.needed_by}
                  onChange={onChange}
                  data-testid="commission-input-date"
                />
                <p className="text-xs text-[#849383] mt-2">
                  At least 3 weeks is ideal — but tighter timelines can be discussed.
                </p>
              </div>
            </div>

            <div>
              <label className="label-eyebrow">Your Vision</label>
              <textarea
                className="earth"
                name="description"
                rows={6}
                value={form.description}
                onChange={onChange}
                required
                placeholder="Subject matter, color scheme, mood — anything you&apos;ve been picturing. We can discuss further over email."
                data-testid="commission-input-description"
              />
            </div>

            <div className="space-y-3">
              <label className="label-eyebrow">Reference Photos (optional)</label>
              <div className="flex flex-wrap gap-3 items-center">
                <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14} />
                  {uploading ? "Uploading…" : "Attach Photos"}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onAttach}
                    disabled={uploading}
                    data-testid="commission-input-attachments"
                  />
                </label>
                {form.reference_image_urls.length > 0 && (
                  <span className="text-xs text-[#849383]">
                    {form.reference_image_urls.length} attached
                  </span>
                )}
              </div>
              {form.reference_image_urls.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3" data-testid="commission-attachments-preview">
                  {form.reference_image_urls.map((url) => (
                    <div key={url} className="relative bg-[#EFECE5] aspect-square">
                      <img src={imgUrl(url)} alt="reference" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeRef(url)}
                        className="absolute top-1 right-1 w-6 h-6 bg-[#1A1F1B]/80 text-white flex items-center justify-center hover:bg-[#1A1F1B]"
                        aria-label="Remove"
                        data-testid="commission-attachment-remove"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={sending}
              className="btn-primary disabled:opacity-50"
              data-testid="commission-submit"
            >
              {sending ? "Sending…" : "Submit Commission Request"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Commissions;
